import {
  CONSENT_WAIT_FOR_UPDATE_MS,
  DEFAULT_DENIED_CONSENT_SIGNALS,
} from './gtmConsentMode';
import type { ConsentSignals } from './gtmConsentMode';

// A `default` command's payload. `wait_for_update` rides inside the signal
// object because it is one more key on the same gtag payload; `region` is a
// sibling so the signal type stays honest, and it is flattened into the
// payload at push time by GoogleTagManager.tsx.
export type ConsentDefaultSignals = ConsentSignals & {
  wait_for_update?: number;
};

export type RegionConsentDefault = {
  signals: ConsentDefaultSignals;
  region?: readonly string[];
};

// `US_GRANTED_SUBDIVISIONS` names every US subdivision (50 states + DC),
// all starting with both analytics and advertising granted (2026-10-19 —
// the former advertising opt-in states were folded in). Since the global
// fallback also grants both, this block is signal-identical to it; it is kept
// as an explicit, named record of which states the site has reviewed. Any US
// location Google resolves at country level only, and every US territory,
// falls through to the same fully-granted global default.
//
// US state privacy laws take effect on a rolling basis, so this list is a
// maintenance surface rather than a settled constant: re-check it whenever a
// new state law's effective date passes. A state that needs an advertising
// opt-in gets its own analytics-only block, like the UK's. The list needs
// legal sign-off before it is treated as authoritative.
export const US_GRANTED_SUBDIVISIONS = [
  'US-AK', 'US-AL', 'US-AR', 'US-AZ', 'US-CA',
  'US-CO', 'US-CT', 'US-DC', 'US-DE', 'US-FL',
  'US-GA', 'US-HI', 'US-IA', 'US-ID', 'US-IL',
  'US-IN', 'US-KS', 'US-KY', 'US-LA', 'US-MA',
  'US-MD', 'US-ME', 'US-MI', 'US-MN', 'US-MO',
  'US-MS', 'US-MT', 'US-NC', 'US-ND', 'US-NE',
  'US-NH', 'US-NJ', 'US-NM', 'US-NV', 'US-NY',
  'US-OH', 'US-OK', 'US-OR', 'US-PA', 'US-RI',
  'US-SC', 'US-SD', 'US-TN', 'US-TX', 'US-UT',
  'US-VA', 'US-VT', 'US-WA', 'US-WI', 'US-WV',
  'US-WY',
] as const;

// Europe is two lists, not one, because the UK's default diverges from the
// EU/EEA's.
//
// The EU/EEA holds at all-denied: ePrivacy Article 5(3) requires prior
// consent for any non-essential storage, Google Analytics does not fit CNIL's
// narrow audience-measurement exemption, and Google's own EU User Consent
// Policy contractually requires consent for analytics storage in the EEA.
// Three independent sources point the same way.
//
// The UK grants `analytics_storage`: the Data (Use and Access) Act 2025 added
// a statutory exception for cookies used solely for statistical purposes to
// improve the service, conditional on clear information and a working
// opt-out. The banner and the Cookie Preferences control are relied on as
// that opt-out.
//
// `GB` therefore lives in `UK_REGIONS` and in no other table — the two lists
// are disjoint by design and the UK is never swept into the EU/EEA denial.
//
// Code convention: the US list uses ISO 3166-2 subdivision codes (`US-CA`),
// the European lists use ISO 3166-1 alpha-2 country codes (`GB`). A bare `CA`
// in a subdivision list would be read by Google as Canada.
export const EU_EEA_REGIONS = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ',
  'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IS', 'IE', 'IT', 'LV', 'LI',
  'LT', 'LU', 'MT', 'NL', 'NO', 'PL',
  'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
] as const;

export const UK_REGIONS = ['GB'] as const;

// Both postures are composed by spreading the denied baseline and overriding
// only what the block grants, so `personalization_storage` stays denied by
// construction — it is never named here and can never be granted by accident.
const FULLY_GRANTED_SIGNALS: ConsentSignals = {
  ...DEFAULT_DENIED_CONSENT_SIGNALS,
  analytics_storage: 'granted',
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
};

// The mixed posture — analytics granted, all three ad_* left at the denied
// baseline — used by the UK.
const ANALYTICS_ONLY_SIGNALS: ConsentSignals = {
  ...DEFAULT_DENIED_CONSENT_SIGNALS,
  analytics_storage: 'granted',
};

// The ordered `default` blocks, pushed in this order before gtm.js loads.
// Order is part of the contract: the global block must land first so it is
// the fallback for every unmatched visitor, and the regional blocks are
// disjoint explicit sets, so a visitor matches at most one of them and
// Google's more-specific-region rule is never load-bearing between them.
//
// Every block spells out all seven consent types, so none of them depends on
// whether gtm.js merges or replaces a region-scoped default per consent type.
// Only the global block carries `wait_for_update`.
export const REGION_CONSENT_DEFAULTS: readonly RegionConsentDefault[] = [
  {
    // The global fallback grants both categories (2026-09-16). Every visitor
    // who matches no regional block — the rest of the world, the six US
    // territories, and any US resolution that stops at country level — lands
    // here, so the EU/EEA and UK blocks below are the places that DENY. `personalization_storage` stays denied because
    // FULLY_GRANTED_SIGNALS never names it.
    signals: {
      ...FULLY_GRANTED_SIGNALS,
      wait_for_update: CONSENT_WAIT_FOR_UPDATE_MS,
    },
  },
  {
    signals: FULLY_GRANTED_SIGNALS,
    region: US_GRANTED_SUBDIVISIONS,
  },
  {
    signals: { ...DEFAULT_DENIED_CONSENT_SIGNALS },
    region: EU_EEA_REGIONS,
  },
  {
    signals: { ...ANALYTICS_ONLY_SIGNALS },
    region: UK_REGIONS,
  },
];
